import { z } from "zod";
import { useContext, useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { toast } from "sonner";
import {
  endPointConfirmAccount,
  endPointCreateAccount,
  endPointResendAccountCode,
} from "modules/main/services/endpoints";
import {
  hasSelectedLocation,
  readUserDataFromLocalStorage,
} from "modules/main/services/location-storage";
import { getVisitorId } from "utils/analytics";
import { AuthContext } from "../context/auth-provider";

/**
 * Site key shared with the legacy `CreateAccountDialog`. Kept in sync so
 * reCAPTCHA tokens issued by the extracted hook are accepted by the same
 * backend reCAPTCHA verifier.
 */
export const USE_CREATE_ACCOUNT_RECAPTCHA_SITE_KEY =
  "6LfdoEgrAAAAALg4NMmdACxKsI9BKCNPEkoi1rP0";

/**
 * Typed shape of the registration form. Mirrors the legacy schema in
 * `loginDialog.tsx` (email + first name + strong password). Exported here
 * so consumers (`CreateAccountForm`) can type their form prop and
 * `loginDialog.tsx` can re-export it without duplication.
 */
export const useCreateAccountSchema = z.object({
  username: z.string().email({ message: "Debe ingresar un email válido" }),
  firstName: z.string().min(1, { message: "El nombre es requerido" }),
  password: z
    .string()
    .min(8, { message: "La contraseña debe tener más de 8 carácteres" })
    .regex(/[a-zA-Z]/, { message: "Debe contener al menos una letra" })
    .regex(/[0-9]/, { message: "Debe contener al menos un número" }),
});

export type CreateAccountSchema = z.infer<typeof useCreateAccountSchema>;

/**
 * Options accepted by `useCreateAccount`.
 *
 * `onAuthenticated` fires only AFTER `checkAuth` resolves successfully
 * (i.e. the OTP confirm returned a valid `user_data` and the backend
 * accepted it). It is the consumer's responsibility to navigate once
 * this callback fires (typically to `/store` for the onboarding flow).
 *
 * `onBackToRegister` is forwarded from the consumer when the user
 * decides to leave the OTP screen and return to the registration form.
 */
export interface UseCreateAccountOptions {
  onAuthenticated: () => void;
  onBackToRegister?: () => void;
}

/**
 * Public contract returned by `useCreateAccount`.
 *
 * Mirrors the legacy `CreateAccountDialog` state shape so the dialog
 * (still consumed by `LoginForm`) and the extracted `CreateAccountForm`
 * remain behavior-compatible.
 */
export interface UseCreateAccountReturn {
  isLoading: boolean;
  confirmMessage: boolean;
  errorMessage: string;
  activationEmail: string;
  otpCode: string;
  setOtpCode: (value: string) => void;
  otpIsInvalid: boolean;
  isResendLoading: boolean;
  resendCooldown: number;
  pwdVisible: boolean;
  setPwdVisible: (value: boolean) => void;
  reCaptchaIsReady: boolean;
  onSubmit: (values: CreateAccountSchema) => Promise<void>;
  onResendCode: () => Promise<void>;
  onVerifyAccount: () => Promise<void>;
  onBackToRegister: () => void;
}

/**
 * Mapped outcome of a backend confirm_account error. The numeric `wait`
 * is the seconds the user must wait before re-trying.
 */
interface ConfirmErrorMapping {
  message: string;
  isOtpInvalid: boolean;
  waitSeconds?: number;
}

/**
 * Executes reCAPTCHA only after the Google SDK has reported readiness.
 * `window.grecaptcha` can exist before it is ready to issue stable v3
 * tokens, so every account action goes through this helper instead of
 * calling `execute` directly.
 */
const executeCreateAccountRecaptcha = async (action: string): Promise<string> => {
  if (!window.grecaptcha) {
    throw new Error("reCAPTCHA no está listo");
  }

  await new Promise<void>((resolve) => {
    window.grecaptcha.ready(resolve);
  });

  return window.grecaptcha.execute(USE_CREATE_ACCOUNT_RECAPTCHA_SITE_KEY, {
    action,
  });
};

/**
 * Maps a non-OK backend confirm_account response into a friendly message
 * and UI state flags. Mirrors the legacy mapper inside
 * `CreateAccountDialog` so behavior is preserved across the extraction.
 */
const mapConfirmAccountError = (
  statusCode: number,
  detail: string | undefined,
): ConfirmErrorMapping => {
  const normalizedDetail = (detail ?? "").toLowerCase();
  const secondsMatch = (detail ?? "").match(/(\d+)/);
  const waitSeconds = secondsMatch ? Number(secondsMatch[1]) : undefined;

  if (statusCode === 429) {
    return {
      message:
        detail ??
        "Demasiados intentos. Espera unos segundos para volver a intentar.",
      isOtpInvalid: false,
      waitSeconds,
    };
  }

  if (statusCode === 400 && normalizedDetail.includes("humano")) {
    return {
      message: "No pudimos validar reCAPTCHA. Intenta nuevamente.",
      isOtpInvalid: false,
    };
  }

  if (
    normalizedDetail.includes("expir") ||
    normalizedDetail.includes("venc")
  ) {
    return {
      message: "El código expiró. Solicita uno nuevo para continuar.",
      isOtpInvalid: true,
    };
  }

  if (
    normalizedDetail.includes("invál") ||
    normalizedDetail.includes("inval") ||
    normalizedDetail.includes("incorrect")
  ) {
    return {
      message:
        "El código ingresado no es válido. Revisa e intenta nuevamente.",
      isOtpInvalid: true,
    };
  }

  return {
    message:
      detail ?? "No fue posible confirmar tu cuenta. Intenta nuevamente.",
    isOtpInvalid: statusCode === 400 || statusCode === 401,
  };
};

/**
 * Hook that owns the account-creation + OTP confirmation state machine.
 *
 * The state machine is intentionally identical to the legacy
 * `CreateAccountDialog` implementation; the extraction is purely a
 * structural refactor. Hook callers receive the same UI state shape and
 * the same auth-success contract.
 */
export const useCreateAccount = (
  options: UseCreateAccountOptions,
): UseCreateAccountReturn => {
  const { onAuthenticated, onBackToRegister } = options;
  const { checkAuth } = useContext(AuthContext);

  const form = useForm<CreateAccountSchema>({
    resolver: zodResolver(useCreateAccountSchema),
    defaultValues: {
      username: "",
      firstName: "",
      password: "",
    },
  });

  const [isLoading, setIsLoading] = useState(false);
  const [confirmMessage, setConfirmMessage] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [activationEmail, setActivationEmail] = useState("");
  const [isResendLoading, setIsResendLoading] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [otpCode, setOtpCode] = useState("");
  const [otpIsInvalid, setOtpIsInvalid] = useState(false);
  const [pwdVisible, setPwdVisible] = useState(false);

  const [reCaptchaIsReady, setReCaptchaIsReady] = useState(
    typeof window.grecaptcha !== "undefined" &&
      Boolean(window.grecaptcha?.ready),
  );

  useEffect(() => {
    const setReady = () => {
      window.grecaptcha?.ready(() => {
        setReCaptchaIsReady(true);
      });
    };

    if (typeof window.grecaptcha !== "undefined") {
      setReady();
      return;
    }

    const scriptSelector = `script[src=\"https://www.google.com/recaptcha/api.js?render=${USE_CREATE_ACCOUNT_RECAPTCHA_SITE_KEY}\"]`;
    const existingScript = document.querySelector(scriptSelector);

    if (!existingScript) {
      const script = document.createElement("script");
      script.src = `https://www.google.com/recaptcha/api.js?render=${USE_CREATE_ACCOUNT_RECAPTCHA_SITE_KEY}`;
      script.async = true;
      document.body.appendChild(script);
    }

    const checkRecaptcha = window.setInterval(() => {
      if (typeof window.grecaptcha !== "undefined") {
        setReady();
        window.clearInterval(checkRecaptcha);
      }
    }, 100);

    return () => {
      window.clearInterval(checkRecaptcha);
    };
  }, []);

  useEffect(() => {
    if (resendCooldown <= 0) {
      return;
    }

    const timer = window.setInterval(() => {
      setResendCooldown((previousValue) => {
        if (previousValue <= 1) {
          window.clearInterval(timer);
          return 0;
        }
        return previousValue - 1;
      });
    }, 1000);

    return () => {
      window.clearInterval(timer);
    };
  }, [resendCooldown]);

  const onSubmit = async (values: CreateAccountSchema): Promise<void> => {
    try {
      setIsLoading(true);

      const recaptchaToken = await executeCreateAccountRecaptcha("createAccount");

      const currentUserData = readUserDataFromLocalStorage();
      const shouldSendLocation = hasSelectedLocation(currentUserData);
      const payload = {
        ...values,
        recaptchaToken,
        ...(shouldSendLocation
          ? { location_id: currentUserData.location_id }
          : {}),
      };

      const response = await fetch(endPointCreateAccount, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "x-visitor-id": getVisitorId(),
        },
        body: JSON.stringify(payload),
        credentials: "include",
      });

      const responseData = (await response.json()) as {
        status: boolean;
        detail: string;
      };

      if (response.status === 200 || response.status === 201) {
        setActivationEmail(values.username);
        setConfirmMessage(true);
        setErrorMessage("");
        setOtpCode("");
        setOtpIsInvalid(false);
        setResendCooldown(60);
        form.reset();
        return;
      }
      setErrorMessage(responseData.detail);
    } catch (error) {
      console.error(error);
      toast.error("Ha ocurrido un problema. Por favor intenta más tarde.");
    } finally {
      setIsLoading(false);
    }
  };

  const onResendCode = async (): Promise<void> => {
    if (!activationEmail || resendCooldown > 0 || isResendLoading) {
      return;
    }

    try {
      setIsResendLoading(true);
      const recaptchaToken = await executeCreateAccountRecaptcha(
        "resendAccountCode",
      );
      const response = await fetch(endPointResendAccountCode, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "x-visitor-id": getVisitorId(),
        },
        body: JSON.stringify({
          username: activationEmail,
          recaptchaToken,
        }),
        credentials: "include",
      });

      const responseData = (await response.json()) as {
        status?: boolean;
        message?: string;
        detail?: string;
      };

      if (response.status === 200) {
        setResendCooldown(60);
        toast.success(
          "Si la cuenta existe, enviaremos un nuevo código de confirmación.",
        );
        return;
      }

      if (response.status === 429) {
        const detailMessage =
          responseData.detail ??
          "Debes esperar 60 segundos para solicitar un nuevo código";
        const secondsMatch = detailMessage.match(/(\d+)/);
        const secondsLeft = secondsMatch ? Number(secondsMatch[1]) : 60;

        setResendCooldown(secondsLeft);
        return;
      }
      toast.error(
        responseData.detail ??
          "No fue posible reenviar el código de confirmación. Intenta nuevamente.",
      );
    } catch (error) {
      toast.error("Ha ocurrido un problema. Por favor intenta más tarde.");
    } finally {
      setIsResendLoading(false);
    }
  };

  const handleBackToRegister = (): void => {
    setConfirmMessage(false);
    setErrorMessage("");
    setOtpCode("");
    setOtpIsInvalid(false);
    setResendCooldown(0);
    onBackToRegister?.();
  };

  const onVerifyAccount = async (): Promise<void> => {
    if (!activationEmail || otpCode.length !== 6) {
      setOtpIsInvalid(true);
      return;
    }

    try {
      setIsLoading(true);
      const recaptchaToken = await executeCreateAccountRecaptcha("confirmAccount");
      const response = await fetch(endPointConfirmAccount, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "x-visitor-id": getVisitorId(),
        },
        body: JSON.stringify({
          username: activationEmail,
          code: otpCode,
          token: otpCode,
          recaptchaToken,
        }),
        credentials: "include",
      });

      const responseData = (await response.json()) as {
        status?: boolean;
        message?: string;
        detail?: string;
        user_data?: string;
      };

      if (response.ok && responseData.status) {
        setOtpIsInvalid(false);
        setErrorMessage("");
        await checkAuth(responseData.user_data);
        toast.info(
          responseData.message ?? "Tu cuenta ha sido confirmada con éxito.",
        );
        onAuthenticated();
        return;
      }

      const mappedError = mapConfirmAccountError(
        response.status,
        responseData.detail ?? responseData.message,
      );
      if (mappedError.waitSeconds && mappedError.waitSeconds > 0) {
        setResendCooldown(mappedError.waitSeconds);
      }
      setOtpIsInvalid(mappedError.isOtpInvalid);
      setErrorMessage(mappedError.message);
    } catch (error) {
      toast.error("Ha ocurrido un problema. Por favor intenta más tarde.");
    } finally {
      setIsLoading(false);
    }
  };

  return {
    isLoading,
    confirmMessage,
    errorMessage,
    activationEmail,
    otpCode,
    setOtpCode,
    otpIsInvalid,
    isResendLoading,
    resendCooldown,
    pwdVisible,
    setPwdVisible,
    reCaptchaIsReady,
    onSubmit,
    onResendCode,
    onVerifyAccount,
    onBackToRegister: handleBackToRegister,
  };
};
